import React, { useEffect, useState } from 'react';
import apiClient, { formatApiError } from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import * as PhosphorIcons from '@phosphor-icons/react';

const { Plus, Trash, Broadcast } = PhosphorIcons;

const Dayparting = () => {
  const [dayparts, setDayparts] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '', start_time: '', end_time: '', playlist_id: '',
    days_of_week: [0, 1, 2, 3, 4, 5, 6], group_ids: []
  });

  const daysOfWeek = [
    { id: 0, name: 'Mon' }, { id: 1, name: 'Tue' }, { id: 2, name: 'Wed' },
    { id: 3, name: 'Thu' }, { id: 4, name: 'Fri' }, { id: 5, name: 'Sat' }, { id: 6, name: 'Sun' }
  ];

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [d, p, g] = await Promise.all([
        apiClient.get('/dayparts'), apiClient.get('/playlists'), apiClient.get('/store-groups')
      ]);
      setDayparts(d.data); setPlaylists(p.data); setGroups(g.data);
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/dayparts', formData);
      toast.success('Daypart created — auto-pushed to all kiosks');
      setOpen(false);
      setFormData({ name: '', start_time: '', end_time: '', playlist_id: '', days_of_week: [0,1,2,3,4,5,6], group_ids: [] });
      fetchData();
    } catch (error) { toast.error(formatApiError(error)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this daypart?')) return;
    try {
      await apiClient.delete(`/dayparts/${id}`);
      toast.success('Daypart deleted — kiosks updated automatically');
      fetchData();
    } catch (error) { toast.error(formatApiError(error)); }
  };

  const handlePushToStores = null; // removed — auto-push on backend

  const toggleDay = (dayId) => {
    setFormData(prev => ({
      ...prev, days_of_week: prev.days_of_week.includes(dayId)
        ? prev.days_of_week.filter(id => id !== dayId) : [...prev.days_of_week, dayId]
    }));
  };

  const toggleGroup = (groupId) => {
    setFormData(prev => ({
      ...prev, group_ids: prev.group_ids.includes(groupId)
        ? prev.group_ids.filter(id => id !== groupId) : [...prev.group_ids, groupId]
    }));
  };

  const getPlaylistName = (id) => playlists.find(p => p.id === id)?.name || 'Unknown';
  const getDayNames = (ids) => ids.map(id => daysOfWeek.find(d => d.id === id)?.name).filter(Boolean).join(', ');
  const getGroupNames = (ids) => {
    if (!ids?.length) return 'All stores';
    return ids.map(id => groups.find(g => g.id === id)?.name).filter(Boolean).join(', ') || 'All stores';
  };

  if (loading) return <div className="text-[#6b665c] font-mono">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="dayparting-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" data-testid="dayparting-title">Dayparting</h1>
          <p className="text-[#6b665c] font-mono text-sm tracking-wider uppercase">Schedule music by time &amp; group</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white border-0" data-testid="add-daypart-button">
              <Plus size={20} className="mr-2" />Add Daypart
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-[#c5d4c8] text-[#2d2a26] max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-mono">Create Daypart Schedule</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="add-daypart-form">
              <div>
                <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Name</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" placeholder="e.g., Morning Vibe" required data-testid="daypart-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Start Time</Label>
                  <Input type="time" value={formData.start_time} onChange={(e) => setFormData({...formData, start_time: e.target.value})} className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" required data-testid="daypart-start-time-input" />
                </div>
                <div>
                  <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">End Time</Label>
                  <Input type="time" value={formData.end_time} onChange={(e) => setFormData({...formData, end_time: e.target.value})} className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" required data-testid="daypart-end-time-input" />
                </div>
              </div>
              <div>
                <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Playlist</Label>
                <Select value={formData.playlist_id} onValueChange={(v) => setFormData({...formData, playlist_id: v})}>
                  <SelectTrigger className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" data-testid="daypart-playlist-select"><SelectValue placeholder="Select playlist" /></SelectTrigger>
                  <SelectContent className="bg-white border-[#c5d4c8] text-[#2d2a26]">
                    {playlists.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f] mb-2 block">Days</Label>
                <div className="flex flex-wrap gap-2">
                  {daysOfWeek.map(day => (
                    <button key={day.id} type="button" onClick={() => toggleDay(day.id)}
                      className={`px-3 py-1.5 rounded-md text-xs font-mono transition-all ${formData.days_of_week.includes(day.id) ? 'bg-blue-600 text-white' : 'bg-[#e4ebe5] text-[#8a8477] hover:bg-[#d9d3c5]'}`}
                      data-testid={`day-btn-${day.id}`}>
                      {day.name}
                    </button>
                  ))}
                </div>
              </div>
              {groups.length > 0 && (
                <div>
                  <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f] mb-2 block">Target Groups (leave empty for all)</Label>
                  <div className="space-y-2 bg-[#f5f0e8] border border-[#c5d4c8] rounded-md p-3 max-h-32 overflow-y-auto">
                    {groups.map(g => (
                      <div key={g.id} className="flex items-center gap-2">
                        <Checkbox id={`group-${g.id}`} checked={formData.group_ids.includes(g.id)} onCheckedChange={() => toggleGroup(g.id)} data-testid={`group-checkbox-${g.id}`} />
                        <label htmlFor={`group-${g.id}`} className="text-sm cursor-pointer">{g.name}</label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white border-0" data-testid="submit-daypart-button">Create Daypart</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#c5d4c8] rounded-md overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#f5f0e8] border-b border-[#c5d4c8]">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-[#6b665c]">Name</th>
              <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-[#6b665c]">Time</th>
              <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-[#6b665c]">Playlist</th>
              <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-[#6b665c]">Days</th>
              <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-[#6b665c]">Groups</th>
              <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-[#6b665c]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {dayparts.map(dp => (
              <tr key={dp.id} className="hover:bg-[#f5f0e8] transition-all" data-testid={`daypart-row-${dp.id}`}>
                <td className="px-6 py-4 font-medium">{dp.name}</td>
                <td className="px-6 py-4 text-[#6b665c] font-mono text-sm">{dp.start_time} - {dp.end_time}</td>
                <td className="px-6 py-4 text-[#6b665c] text-sm">{getPlaylistName(dp.playlist_id)}</td>
                <td className="px-6 py-4 text-[#6b665c] text-xs">{getDayNames(dp.days_of_week)}</td>
                <td className="px-6 py-4 text-[#6b665c] text-xs">{getGroupNames(dp.group_ids)}</td>
                <td className="px-6 py-4">
                  <Button onClick={() => handleDelete(dp.id)} className="bg-[#fce8eb] hover:bg-[#f0c4cb] text-[#b5636e] border-0" data-testid={`delete-daypart-${dp.id}`}>
                    <Trash size={16} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {dayparts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-[#8a8477] font-mono">No dayparts yet. Create a schedule to manage music throughout the day.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dayparting;
